import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { promptsAPI, votesAPI, favoritesAPI } from '../services/api';
import { SkeletonDetail } from './Skeleton';
import './PromptDetail.css';

function PromptDetail({ id, currentUsername, showToast }) {
  const [prompt, setPrompt] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isFavorite, setIsFavorite] = useState(false);
  const [isVoting, setIsVoting] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    loadPrompt();
  }, [id]);

  useEffect(() => {
    if (currentUsername && prompt?.id) {
      checkFavorite();
    }
  }, [currentUsername, prompt?.id]);

  const loadPrompt = async () => {
    try {
      setLoading(true);
      const response = await promptsAPI.getById(id);
      setPrompt(response.data);
    } catch (err) {
      console.error('Failed to load prompt:', err);
    } finally {
      setLoading(false);
    }
  };

  const checkFavorite = async () => {
    try {
      const response = await favoritesAPI.getAll(currentUsername);
      const favorites = Array.isArray(response.data) ? response.data : [];
      setIsFavorite(favorites.some(f => f.id === prompt.id));
    } catch (err) {
      // Ignore
    }
  };

  const handleVote = async (voteType) => {
    if (!currentUsername) {
      showToast('Please set your username first', 'error');
      return;
    }

    if (isVoting) return; // Prevent double voting

    // Store original values for rollback
    const originalLikes = prompt.likes || 0;
    const originalDislikes = prompt.dislikes || 0;
    const originalPrompt = { ...prompt };

    // Optimistic update - immediately update UI
    setIsVoting(true);
    setPrompt(prev => {
      if (!prev) return prev;
      
      // Check if user already voted this type (toggle behavior)
      // For now, just increment optimistically
      const newLikes = voteType === 'like' ? prev.likes + 1 : prev.likes;
      const newDislikes = voteType === 'dislike' ? prev.dislikes + 1 : prev.dislikes;
      
      return {
        ...prev,
        likes: newLikes,
        dislikes: newDislikes
      };
    });

    try {
      await votesAPI.vote({
        prompt_id: parseInt(id),
        username: currentUsername,
        vote_type: voteType,
      });
      // Reload to get accurate counts (handles toggle logic from server)
      loadPrompt();
    } catch (err) {
      // Rollback on error
      setPrompt(originalPrompt);
      showToast('Failed to vote. Please try again.', 'error');
      console.error('Failed to vote:', err);
    } finally {
      setIsVoting(false);
    }
  };

  const toggleFavorite = async () => {
    if (!currentUsername) {
      showToast('Please set your username first', 'error');
      return;
    }

    try {
      if (isFavorite) {
        await favoritesAPI.remove(prompt.id, currentUsername);
        setIsFavorite(false);
        showToast('Removed from favorites', 'info');
      } else {
        await favoritesAPI.add({ prompt_id: prompt.id, username: currentUsername });
        setIsFavorite(true);
        showToast('Added to favorites', 'success');
      }
    } catch (err) {
      showToast('Failed to update favorites', 'error');
      console.error('Failed to toggle favorite:', err);
    }
  };

  const copyPrompt = async () => {
    const text = prompt?.prompt_text || prompt?.promptText || '';
    try {
      await navigator.clipboard.writeText(text);
      showToast('Prompt copied to clipboard!', 'success');
    } catch (err) {
      // Fallback for older browsers
      const textArea = document.createElement('textarea');
      textArea.value = text;
      document.body.appendChild(textArea);
      textArea.select();
      try {
        document.execCommand('copy');
        showToast('Prompt copied to clipboard!', 'success');
      } catch (e) {
        showToast('Failed to copy. Please select and copy manually.', 'error');
      }
      document.body.removeChild(textArea);
    }
  };

  if (loading) {
    return <div className="container"><SkeletonDetail /></div>;
  }

  if (!prompt) {
    return <div className="container"><div className="error">Prompt not found</div></div>;
  }

  return (
    <div className="container">
      <button onClick={() => navigate(-1)} className="back-button">← Back</button>
      <div className="prompt-detail">
        <h1>{prompt.title}</h1>
        <div className="prompt-meta">
          <div className="category-tags">
            {(prompt.category || '').split(',').map((tag, idx) => {
              const trimmedTag = tag.trim();
              return trimmedTag ? (
                <span key={idx} className="tag-pill">{trimmedTag}</span>
              ) : null;
            })}
          </div>
          <span className="username">by {prompt.username}</span>
          <span className="date">
            Created: {new Date(prompt.created_at || prompt.createdAt).toLocaleDateString()}
            {(prompt.isEdited || (prompt.updated_at && prompt.updated_at !== prompt.created_at)) && (
              <span className="edited-badge">
                • Edited: {new Date(prompt.updated_at || prompt.updatedAt).toLocaleDateString()}
              </span>
            )}
          </span>
        </div>
        {(prompt.model || prompt.tone_style || prompt.toneStyle || prompt.use_case || prompt.useCase) && (
          <div className="prompt-context">
            {prompt.model && (
              <div className="context-item">
                <strong>Model:</strong> {prompt.model}
              </div>
            )}
            {(prompt.tone_style || prompt.toneStyle) && (
              <div className="context-item">
                <strong>Tone/Style:</strong> {prompt.tone_style || prompt.toneStyle}
              </div>
            )}
            {(prompt.use_case || prompt.useCase) && (
              <div className="context-item">
                <strong>Use Case:</strong> {prompt.use_case || prompt.useCase}
              </div>
            )}
          </div>
        )}
        <div className="prompt-text plain-text">
          {prompt.prompt_text || prompt.promptText || ''}
        </div>
        <div className="prompt-actions">
          <button 
            onClick={copyPrompt} 
            className="button button-primary"
            disabled={isVoting}
          >
            Copy Prompt
          </button>
          {currentUsername && (
            <>
              <button
                onClick={() => handleVote('like')}
                className="button"
                disabled={isVoting}
              >
                👍 Like ({prompt.likes || 0})
              </button>
              <button
                onClick={() => handleVote('dislike')}
                className="button"
                disabled={isVoting}
              >
                👎 Dislike ({prompt.dislikes || 0})
              </button>
              <button
                onClick={toggleFavorite}
                className={`button ${isFavorite ? 'active' : ''}`}
                disabled={isVoting}
              >
                {isFavorite ? 'Saved' : 'Save'}
              </button>
              {currentUsername === prompt.username && (
                <button
                  onClick={() => navigate(`/prompts/${id}/edit`)}
                  className="button"
                  disabled={isVoting}
                >
                  ✏️ Edit
                </button>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export default PromptDetail;
